import React, { memo, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📘',
  number: 'Component 1',
  title: 'Research Foundations & the Purpose of Investigation',
  subtitle: 'The base your students need to understand everything else',
  problem: 'The first contact with research methodology can be the most transformative or the most boring lesson of the year. This component turns a technical subject into something understandable and relevant for your students from day one.',
  bullets: ['How to teach what research methodology is and why it matters', 'How to guide students through the main elements of academic research', 'How to present the research process as a logical, ordered system', 'Pedagogical strategies, classroom language and teacher intervention notes included'],
  result: 'Your students understand research as a logical, ordered process — not as a collection of impossible terms to memorize.'
}, {
  icon: '📗',
  number: 'Component 2',
  title: 'Research Questions, Objectives & Problem Formulation',
  subtitle: 'The starting point of every well-structured investigation',
  problem: 'This is one of the points where students get most stuck. This component gives you everything ready so students can learn it with clarity and confidence.',
  bullets: ['How to teach students to identify and define a research problem', 'How to guide students in formulating strong research questions', "How to build general and specific objectives using Bloom's Taxonomy", 'How to teach the justification and relevance of a study'],
  result: 'Your students learn to define clearly what they are investigating, why and for what purpose — without going in circles.'
}, {
  icon: '📙',
  number: 'Component 3',
  title: 'Literature Review & Theoretical Framework',
  subtitle: 'How to teach students to build a solid academic foundation',
  problem: 'The theoretical framework is usually the section that confuses students the most. This component gives you the material to explain it in an organized, comprehensible way.',
  bullets: ['How to teach students to find and evaluate academic sources', 'How to guide the construction of a theoretical framework', 'How to organize information logically and academically', 'How to teach proper citation and paraphrase in APA and MLA formats'],
  result: 'Your students learn to support their research with solid, well-organized academic foundations.'
}, {
  icon: '📕',
  number: 'Component 4',
  title: 'Research Design, Approaches & Types of Investigation',
  subtitle: 'The module that generates the most confusion — and is best solved right here',
  problem: 'This is the most complex core of research methodology. Here your students learn to distinguish between approaches and choose the correct one for their investigation.',
  bullets: ['How to teach quantitative, qualitative and mixed methods research', 'How to explain exploratory, descriptive, correlational and explanatory research', 'How to present experimental, quasi-experimental and non-experimental designs', 'How to teach cross-sectional and longitudinal designs'],
  result: 'Your students stop confusing concepts and learn to choose the correct approach based on their research topic.',
  popular: true
}, {
  icon: '📓',
  number: 'Component 5',
  title: 'Population, Sampling, Data Collection & Research Instruments',
  subtitle: 'Teach students to collect information correctly',
  problem: 'One of the most practical points in methodology. This component gives your students the tools to understand how to obtain valid and reliable data.',
  bullets: ['How to teach the difference between population and sample', 'How to explain sampling types and when to use each one', 'How to present data collection techniques and their applications', 'How to teach survey and interview design as research instruments'],
  result: 'Your students understand how to obtain methodologically correct and useful information for their investigations.'
}, {
  icon: '📔',
  number: 'Component 6',
  title: 'Data Analysis, Interpretation & Presentation of Results',
  subtitle: 'The closing stage that completes the entire research process',
  problem: 'Many students reach the end of their investigation without knowing how to interpret what they found. This component teaches them to close the process with clarity and academic confidence.',
  bullets: ['How to teach data organization and analysis', 'How to guide correct interpretation of results', 'How to teach the connection between results, objectives and hypothesis', 'How to build strong, coherent conclusions'],
  result: 'Your students close their investigation with clarity, coherence and full methodological confidence.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-black" data-id="element-281">
      <div className="max-w-6xl mx-auto" data-id="element-282">
        <div className="text-center mb-16" data-id="element-283">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-284">
            📘 The 6 Components of Research Methods Ready™
          </h2>
          <p className="text-xl text-gold-light" data-id="element-285">
            Complete classroom teaching material
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-286">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-dark-card rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-dark-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-287">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-288">
                  MOST POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-289">
                <span className="text-4xl" data-id="element-290">{tomo.icon}</span>
                <div data-id="element-291">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-292">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1" data-id="element-293">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-294">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-295">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-296">
                  What does it solve in the classroom?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-297">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-298">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-299">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-300" />
                    <span className="text-gray-200 text-sm" data-id="element-301">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-302">
                <span className="text-gold font-bold block mb-1" data-id="element-303">Result:</span>
                <p className="text-white text-sm" data-id="element-304">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}